import React, { Component } from 'react';
import config from 'config'
import { FormattedMessage } from 'translations';
import { Field } from 'redux-form';
import TranslatedField from 'app/components/common/TranslatedField';
import builder from 'app/components/search-results/SearchForm/builder';
import { SwapLocations } from './elements';

import {
  ROUND_TRIP_MODE_VALUE
} from 'app/constants';

// new ui-kit components
import AutofocusElement from 'mz-ui-kit/core/AutofocusElement';

import SwapIcon from 'mz-icons/swap'
import Row, { Cell } from 'mz-ui-kit/core/Row';
import Button from 'mz-ui-kit/core/Button';
import PlaceField from 'mz-ui-kit/form/PlaceField';
import RideDurationField from 'mz-ui-kit/form/RideDurationField';
import DateField from 'mz-ui-kit/form/DateField';
import TimeField from 'mz-ui-kit/form/TimeField';
import PassengersNumberField from 'mz-ui-kit/form/PassengersNumberField';
import SwitchField from 'mz-ui-kit/form/SwitchField';
import ParkingForm from './ParkingForm';
import Tabs from './Tabs';
import {
  Container,
  Form,
  Fields,
  PickupDateTime,
  Mode,
  ButtonWrapper,
  SearchText,
  SwapContainer,
  StyledAdsense
} from './elements';
import { HomePageContext } from 'app/pages/HomePage/HomePageContext';
import posthog from 'posthog-js';
import moment from 'moment';

const messages = {
  submit: { id: 'SEARCH_FORM.SEARCH_BUTTON' },
  startLabel: { id: 'SEARCH_FORM.FROM_ADDRESS' },
  roundtrip: { id: 'SEARCH_FORM.ROUND_TRIP' },
  endLabel: { id: 'SEARCH_FORM.TO_ADDRESS' },
  numPassengersLabel: { id: 'SEARCH_FORM.NUMBER_PASSENGERS' },
  addressPlaceholder: { id: config.ENABLE_TRAIN_WORDING
    ? 'SEARCH_FORM.ADDRESS_PLACEHOLDER_TRAIN'
    : 'SEARCH_FORM.ADDRESS_PLACEHOLDER'
  },
  pickupDate: { id: 'SEARCH_FORM.PICKUP_DATE' },
  pickupTime: { id: 'SEARCH_FORM.PICKUP_TIME' },
  flightDate: { id: 'SEARCH_FORM.FLIGHT_DATE' },
  flightTime: { id: 'SEARCH_FORM.FLIGHT_TIME' },
  returnPickupDate: { id: 'SEARCH_FORM.RETURN_PICKUP_DATE' },
  returnPickupTime: { id: 'SEARCH_FORM.RETURN_PICKUP_TIME' },
  returnFlightDate: { id: 'SEARCH_FORM.RETURN_FLIGHT_DATE' },
  returnFlightTime: { id: 'SEARCH_FORM.RETURN_FLIGHT_TIME' },
  pointToPoint: { id: 'SEARCH_FORM.POINT_TO_POINT' },
  hourlyRide: { id: 'SEARCH_FORM.HOURLY_RIDE' },
  rideDuration: { id: 'SEARCH_FORM.RIDE_DURATION' },
  yes: { id: 'SEARCH_FORM.ROUND_TRIP_YES' },
  no: { id: 'SEARCH_FORM.ROUND_TRIP_NO' },
};


const captureEvent = (eventType) => {
  posthog.capture(eventType);
}

const DEFAULT_LOCATION_BIAS = config.PLACE_AUTOCOMPLETE_PARAMS
  .reduce((acc, p) => ({ ...acc, [p[0]]: p[1] }), {})

export class SearchForm extends Component {
  static contextType = HomePageContext;

  componentDidMount() {
    this.props.loadSearchForm();
  }

  get isHourlyRide() {
    const { booking_mode } = this.props;
    return booking_mode === 'hourly';
  }

  get isParking() {
    const { booking_mode } = this.props;
    return booking_mode === 'parking';
  }

  componentDidUpdate(prevProps) {
    const { pickup_datetime, return_pickup_datetime, change } = this.props;
    if (
      pickup_datetime &&
      !pickup_datetime.isSame(prevProps.pickup_datetime) &&
      !return_pickup_datetime
    ) {
      change('return_pickup_datetime', moment(pickup_datetime).add(3, 'days'));
    }
  }

  renderRoundtrip() {
    const { mode, useReturnLandingTime, pickup_datetime } = this.props;
    if (this.isHourlyRide || mode !== ROUND_TRIP_MODE_VALUE) return null;

    const returnDateLabel = useReturnLandingTime
      ? messages.returnFlightDate
      : messages.returnPickupDate;

    const returnTimeLabel = useReturnLandingTime
      ? messages.returnFlightTime
      : messages.returnPickupTime;

    return (
      <PickupDateTime>
        <Row color="transparent" wrap="xs">
          <TranslatedField
            component={DateField}
            id="return_pickup_date"
            name="return_pickup_datetime"
            appearance="rect"
            fieldAppearnce="desktop"
            label={<FormattedMessage {...returnDateLabel} />}
            placeholder={<FormattedMessage {...returnDateLabel} />}
            lightboxSubtitle={<span />}
            cleanable={false}
            minDate={pickup_datetime}
          />
          <TranslatedField
            component={TimeField}
            id="return_pickup_time"
            name="return_pickup_datetime"
            appearance="rect"
            fieldAppearnce="desktop"
            label={<FormattedMessage {...returnTimeLabel} />}
            placeholder={<FormattedMessage {...returnTimeLabel} />}
            lightboxSubtitle={<span />}
            cleanable={false}
          />
        </Row>
      </PickupDateTime>
    );
  }

  interacted = false;
  handleInteract = (fieldName) => {
    if (!this.interacted) {
      this.interacted = true;
      this.context.captureSearchStarted(fieldName);
    }
  };

  handleSearchSubmit = (values) => {
    const { doSearch } = this.props;
    this.context.captureSearchSubmit({
      start_address: values.start_address.display,
      start_address_type: values.start_address.type,
      end_address: values.end_address?.display,
      end_address_type: values.end_address?.type,
      pickup_date: values.pickup_datetime.format('YYYY-MM-DD'),
      pickup_time: values.pickup_datetime.format('HH:mm:ss'),
      is_landing_time_selected: values.is_flight_time,
      passenger_count: values.num_passengers,
      is_returm_added: values.mode === ROUND_TRIP_MODE_VALUE,
      return_pickup_date: values.return_pickup_datetime?.format('YYYY-MM-DD'),
      return_pickup_time: values.return_pickup_datetime?.format('HH:mm:ss'),
      start_address_id: values.start_address.place_id,
      end_address_id: values.end_address?.place_id,
      is_return_landing_time_selected: values.is_return_flight_time,
      is_hourly_ride: this.isHourlyRide,
    });
    doSearch(values);
  };

  handleTabChange = (value) => {
    if (value === 'hourly') {
      captureEvent('Hourly Ride Tab Clicked');
    }
    if (value === 'parking') {
      captureEvent('Parking Tab Clicked');
    }
  };

  handleSearchButtonClick = () => {
    const errors = this.props.validate(this.props);
    const error_fields = Object.entries(errors).map(([key]) => key);
    const error_count = error_fields.length;

    if (error_count) {
      posthog.capture('Search Form Error', { error_fields, error_count });
    }
  };

  render() {
    const {
      title,
      hourlyTitle,
      handleSubmit,
      swapLocations,
      useLandingTime,
      start_address,
      end_address,
      placeParams,
      esimUrl,
      parkingUrl,
    } = this.props;

    const actualTitle = this.isHourlyRide ? hourlyTitle : title;
    const addressPlaceholder = messages.addressPlaceholder;
    const dateLabel = useLandingTime
      ? messages.flightDate
      : messages.pickupDate;
    const timeLabel = useLandingTime
      ? messages.flightTime
      : messages.pickupTime;

    return (
      <AutofocusElement
        render={({ setFocusableRef }) => (
          <Container>
            <Tabs
              esimUrl={esimUrl}
              parkingUrl={parkingUrl}
              onChange={this.handleTabChange}
            />
            {this.isParking && (
              <ParkingForm
                handleSubmit={handleSubmit}
                handleSearchButtonClick={this.handleSearchButtonClick}
              />
            )}
            {!this.isParking && (
              <Form onSubmit={handleSubmit(this.handleSearchSubmit)}>
                {actualTitle || title}
                <Fields>
                  <Row color="transparent" wrap="xs">
                    <SwapContainer>
                      <TranslatedField
                        component={PlaceField}
                        id="start_address"
                        name="start_address"
                        focusableRef={setFocusableRef}
                        appearance="rect"
                        fieldAppearnce="desktop"
                        label={<FormattedMessage {...messages.startLabel} />}
                        placeholder={
                          <FormattedMessage {...addressPlaceholder} />
                        }
                        bias={{ ...end_address, ...DEFAULT_LOCATION_BIAS }}
                        otherParams={placeParams}
                        searchableOptions={{
                          onQueryChange: () =>
                            this.handleInteract('start_address'),
                        }}
                      />
                      {!this.isHourlyRide && (
                        <SwapLocations onClick={swapLocations}>
                          <SwapIcon />
                        </SwapLocations>
                      )}
                    </SwapContainer>
                    {!this.isHourlyRide && (
                      <TranslatedField
                        component={PlaceField}
                        id="end_address"
                        name="end_address"
                        appearance="rect"
                        fieldAppearnce="desktop"
                        label={<FormattedMessage {...messages.endLabel} />}
                        placeholder={
                          <FormattedMessage {...addressPlaceholder} />
                        }
                        bias={{ ...start_address, ...DEFAULT_LOCATION_BIAS }}
                        otherParams={{
                          ...placeParams,
                          strictbounds: true,
                          lat: start_address?.lat,
                          lng: start_address?.lng,
                        }}
                        searchableOptions={{
                          onQueryChange: () =>
                            this.handleInteract('end_address'),
                        }}
                      />
                    )}
                    {this.isHourlyRide && (
                      <TranslatedField
                        component={RideDurationField}
                        name="hourly_booking_duration"
                        appearance="rect"
                        label={<FormattedMessage {...messages.rideDuration} />}
                      />
                    )}
                  </Row>
                  <PickupDateTime>
                    <Row color="transparent" wrap="xs">
                      <TranslatedField
                        component={DateField}
                        id="pickup_date"
                        name="pickup_datetime"
                        appearance="rect"
                        fieldAppearnce="desktop"
                        label={<FormattedMessage {...dateLabel} />}
                        placeholder={<FormattedMessage {...dateLabel} />}
                        lightboxSubtitle={<span />}
                        cleanable={false}
                        onFocus={() => this.handleInteract('pickup_date')}
                      />
                      <TranslatedField
                        component={TimeField}
                        id="pickup_time"
                        name="pickup_datetime"
                        appearance="rect"
                        fieldAppearnce="desktop"
                        label={<FormattedMessage {...timeLabel} />}
                        placeholder={<FormattedMessage {...timeLabel} />}
                        lightboxSubtitle={<span />}
                        cleanable={false}
                        onFocus={() => this.handleInteract('pickup_time')}
                      />
                    </Row>
                  </PickupDateTime>
                  <Row color="transparent">
                    <Cell>
                      <Field
                        name="num_passengers"
                        appearance="rect"
                        label={
                          <FormattedMessage {...messages.numPassengersLabel} />
                        }
                        component={PassengersNumberField}
                        onChange={() => this.handleInteract('num_passengers')}
                      />
                    </Cell>
                    <Cell>
                      {!this.isHourlyRide && (
                        <Mode>
                          <Field
                            component={SwitchField}
                            name="mode"
                            appearance="primary-inverse"
                            label={<FormattedMessage {...messages.roundtrip} />}
                            verbose
                            activeValue="round_trip"
                            inactiveValue="one_way"
                            activeLabel={<FormattedMessage {...messages.yes} />}
                            inactiveLabel={
                              <FormattedMessage {...messages.no} />
                            }
                            onChange={() => this.handleInteract('mode')}
                          />
                        </Mode>
                      )}
                    </Cell>
                  </Row>
                  {this.renderRoundtrip()}
                </Fields>
                <ButtonWrapper>
                  <Button
                    TextWrapper={SearchText}
                    color="cta"
                    type="submit"
                    onClick={this.handleSearchButtonClick}
                    fullWidth
                  >
                    <FormattedMessage {...messages.submit} />
                  </Button>
                </ButtonWrapper>
              </Form>
            )}
            <StyledAdsense
              client="ca-pub-5731973738783459"
              slot="8192939079"
              format="fluid"
              responsive="false"
              layoutKey="-fb+5w+4e-db+86"
            />
          </Container>
        )}
      />
    );
  }
}

// TODO: move to Flow types
// SearchForm.propTypes = {
//   ...propTypes,
//   title: PropTypes.node,
//   doSearch: PropTypes.func.isRequired,
//   swapLocations: PropTypes.func.isRequired,
//   loadSearchForm: PropTypes.func.isRequired,
//   start_address: PropTypes.object,
//   end_address: PropTypes.object,
//   mode: PropTypes.string,
//   booking_mode: PropTypes.string,
//   useLandingTime: PropTypes.bool,
//   useReturnLandingTime: PropTypes.bool
// };

export default builder(SearchForm);
